import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import includes from 'lodash/includes';
import debounce from 'lodash/debounce';
import { gettext } from '@eb/i18n';

import Button from 'eventbrite_design_system/button/Button';
import Icon from 'eventbrite_design_system/icon/Icon';
import MagnifyingGlassChunkySvg from 'eventbrite_design_system/iconography/icons/MagnifyingGlassChunky';

import {
    addWindowListener,
    removeWindowListener,
    HAS_WINDOW,
} from 'eventbrite_design_system/utils/dom';

import { storeQuery } from '@eb/destination-utils';
import {
    SUGGESTIONS_PROPTYPE,
    CATEGORY_DROPDOWN_SELECT,
    EVENTS_TYPE,
    ALL_EVENTS_TYPE,
} from '../../constants';

import SearchAutocomplete from './SearchAutocomplete';
import ConnectedLocationAutocomplete from '../../containers/ConnectedLocationAutocomplete';

import './searchInput.scss';

const LOCATION_DEBOUNCE_INTERVAL = 200;
const PAGE_SHOW_EVENT_NAME = 'pageshow';

export default class SearchInput extends PureComponent {
    static propTypes = {
        handleApplyFilter: PropTypes.func.isRequired,
        handleLocationSuggestionChange: PropTypes.func.isRequired,
        handleSearchSuggestionChange: PropTypes.func.isRequired,
        currentPlace: PropTypes.string,
        initialSearchQuery: PropTypes.string,
        suggestions: SUGGESTIONS_PROPTYPE,
        handleLocationSelect: PropTypes.func,
        handleUseCurrentLocation: PropTypes.func,
        onGoogleMapsLoaded: PropTypes.func,
    };

    constructor(props) {
        super(props);

        this.state = {
            searchQuery: props.initialSearchQuery,
            shouldFocusInput: false,
        };

        this._boundHandlePageLoadFromCache = this._handlePageLoadFromCache;
    }

    componentDidMount() {
        addWindowListener(
            PAGE_SHOW_EVENT_NAME,
            this._boundHandlePageLoadFromCache,
        );
    }

    // eslint-disable-next-line camelcase
    UNSAFE_componentWillReceiveProps({
        initialSearchQuery: newInitialSearchQuery,
    }) {
        if (newInitialSearchQuery !== this.props.initialSearchQuery) {
            this.setState({ searchQuery: newInitialSearchQuery });
        }
    }

    componentWillUnmount() {
        removeWindowListener(
            PAGE_SHOW_EVENT_NAME,
            this._boundHandlePageLoadFromCache,
        );
    }

    /**
     * Helper function which consumes the `pageshow` event to determine if the
     * page was served from the browser cache. Allows for a hook such that any
     * state resetting or other behaviors can occur.
     */
    _handlePageLoadFromCache = (event) => {
        const { currentPlace, initialSearchQuery } = this.props;

        if (event.persisted) {
            this.setState({
                searchQuery: initialSearchQuery,
            });

            //For browsers which utilize back-forward cache, the store is cached with the
            //autocomplete suggestions previously entered by the user so we want to display
            //suggestions which make sense in the context of the current state.
            this._requestLocationSuggestions(currentPlace);
        }
    };

    _handleSubmit = () => {
        const { handleApplyFilter, hasHistory } = this.props;

        let { searchQuery } = this.state;

        let newQuery = searchQuery;

        if (hasHistory && HAS_WINDOW) {
            storeQuery(searchQuery);
        }

        // when searching for 'events' or 'all events' input should be resetted
        if (includes([EVENTS_TYPE, ALL_EVENTS_TYPE], searchQuery)) {
            newQuery = '';
        }

        // when searching term matches category dropdown input and query should be resetted and
        if (searchQuery === CATEGORY_DROPDOWN_SELECT) {
            searchQuery = '';
            newQuery = '';
        }

        this.setState(
            {
                searchQuery: newQuery,
            },
            () => {
                handleApplyFilter({ q: searchQuery });
            },
        );
    };

    _handleSearchSuggestionChange = (searchQuery) => {
        const { handleSearchSuggestionChange } = this.props;

        handleSearchSuggestionChange(searchQuery);
    };

    _handleApplySearchFilter = (searchFilter) => {
        const { handleApplyFilter } = this.props;
        const { searchQuery } = this.state;

        let newQuery = searchQuery;
        const { q, tags } = searchFilter;

        if (q) {
            newQuery = q;
        }

        // Special case where the user selects a category from the
        // dropdown. Need to ensure a re-render occurs so utilizing
        // a whitelisted value to pass through.
        if (q === '' && tags) {
            newQuery = CATEGORY_DROPDOWN_SELECT;

            if (searchQuery === CATEGORY_DROPDOWN_SELECT) {
                newQuery = '';
            }
        }

        // when searching for 'events' or 'all events' input should be resetted
        if (includes([EVENTS_TYPE, ALL_EVENTS_TYPE], (q || '').toLowerCase())) {
            newQuery = '';
        }

        this.setState(
            {
                searchQuery: newQuery,
            },
            () => handleApplyFilter(searchFilter),
        );
    };

    _handleSearchQueryChange = (searchQuery) => {
        this.setState({ searchQuery });
    };

    _handleSearchFieldFocus = () => {
        this.setState({
            shouldFocusInput: true,
        });
    };

    _handleSearchFieldBlur = () => {
        this.setState({
            shouldFocusInput: false,
        });
    };

    _handleClearButtonClick = () => {
        this.setState({
            searchQuery: '',
            shouldFocusInput: true,
        });
    };

    _requestLocationSuggestions = debounce((locationQuery) => {
        const { handleLocationSuggestionChange } = this.props;

        handleLocationSuggestionChange(locationQuery);
    }, LOCATION_DEBOUNCE_INTERVAL);
    render() {
        const {
            suggestions,
            hasHistory,
            trackGAEvent,
            handleUseCurrentLocation,
            handleLocationSelect,
            onGoogleMapsLoaded,
            history,
        } = this.props;
        const { searchQuery, shouldFocusInput } = this.state;

        return (
            <form autoComplete="none">
                <nav className="search-input">
                    <div className="search-input__query">
                        <SearchAutocomplete
                            hasHistory={hasHistory}
                            searchQuery={searchQuery}
                            suggestions={suggestions}
                            onSearchSuggestionChange={
                                this._handleSearchSuggestionChange
                            }
                            onSearchQueryChange={this._handleSearchQueryChange}
                            onSearchFilterChange={this._handleApplySearchFilter}
                            onClearButtonClick={this._handleClearButtonClick}
                            onFocus={this._handleSearchFieldFocus}
                            onBlur={this._handleSearchFieldBlur}
                            shouldFocusInput={shouldFocusInput}
                            trackGAEvent={trackGAEvent}
                            history={history}
                        />
                        <div className="eds-show-up-sw eds-l-pad-top-1">
                            <Button
                                data-spec="search-button"
                                onClick={this._handleSubmit}
                                style="fill"
                                type="button"
                            >
                                {gettext('Search')}
                            </Button>
                        </div>
                        <div className="eds-show-down-sm">
                            {/*<IconButton
                                title={gettext('Search')}
                                style="brand"
                                iconType={<MagnifyingGlassChunkySvg />}
                                onClick={this._handleSubmit}
                            />*/}
                            <Button
                                style="fill"
                                type="button"
                                onClick={this._handleSubmit}
                                __containerClassName="search-input__query-submit"
                            >
                                <Icon type={<MagnifyingGlassChunkySvg />} />
                            </Button>
                        </div>
                    </div>
                    <div className="eds-align--center-vertical">
                        <div className="search-input__span eds-text-hm eds-show-up-sw eds-l-mar-left-4 eds-l-pad-bot-2 eds-text-color--grey-400">
                            {gettext('in')}
                        </div>
                        <ConnectedLocationAutocomplete
                            handleUseCurrentLocation={handleUseCurrentLocation}
                            handleLocationSelect={handleLocationSelect}
                            onGoogleMapsLoaded={onGoogleMapsLoaded}
                            history={history}
                        />
                    </div>
                </nav>
            </form>
        );
    }
}
